import { ArchiveIcon } from "@heroicons/react/outline";

import {
  PendingDeletesByLabelId,
  PendingReleaseLabelMovesRegistry,
} from "./hooks/usePendingReleaseLabelMoves";
import VersionTab from "./VersionTab";
import { Label } from "./VersionTab/types";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type {
  VersionSelectorVersion,
  VersionSelectorVersionId,
} from "@/components/VersionSelector/types";
import { EntityType } from "@/queries/folder-entities";
import { ReportScore } from "@/types/evaluate";

interface ArchiveGroupProps {
  count: number;
  groupIndex: number;
  archivedVersions: VersionSelectorVersion[];
  isExpanded: boolean;
  onToggle: () => void;
  versionNumber: string | undefined;
  augmentedVersions: VersionSelectorVersion[];
  counts: Record<number, number>;
  navigateToVersionLink: (versionNumber: number) => string;
  onAddReleaseLabel?: (label: string, versionId: VersionSelectorVersionId) => Promise<unknown>;
  handleDynamicReleaseLabelClick: (release_label_group_id: number) => void;
  dynamicReleaseLabelsRegistry?: Record<number, Label[]>;
  versionEvaluationScoresRegistry?: Record<number, ReportScore | null>;
  handleABTestButtonClick: (promptId: number, promptVersionId: number) => void;
  handleBacktestButtonClick: (promptVersionId: number) => void;
  handleArchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  handleUnarchiveButtonClick: (promptVersionId: VersionSelectorVersionId) => void;
  archivingVersionId: VersionSelectorVersionId | null;
  isArchiving: boolean;
  promptId: string | undefined;
  isWorkflow?: boolean;
  entityType?: EntityType;
  onConfigureLabel?: (label: Label) => void;
  allLabels: unknown[];
  pendingReleaseLabelMoves?: PendingReleaseLabelMovesRegistry;
  pendingDeletesByLabelId?: PendingDeletesByLabelId;
  onPendingLabelClick?: (changelogId: number) => void;
}

const ArchiveGroup = ({
  count,
  groupIndex,
  isExpanded,
  onToggle,
  archivedVersions,
  versionNumber,
  augmentedVersions,
  counts,
  navigateToVersionLink,
  onAddReleaseLabel,
  handleDynamicReleaseLabelClick,
  dynamicReleaseLabelsRegistry,
  versionEvaluationScoresRegistry,
  handleABTestButtonClick,
  handleBacktestButtonClick,
  handleArchiveButtonClick,
  handleUnarchiveButtonClick,
  archivingVersionId,
  isArchiving,
  promptId,
  isWorkflow = false,
  entityType,
  onConfigureLabel,
  allLabels,
  pendingReleaseLabelMoves,
  pendingDeletesByLabelId,
  onPendingLabelClick,
}: ArchiveGroupProps) => (
  <Accordion type="single" collapsible value={isExpanded ? `item-${groupIndex}` : ""}>
    <AccordionItem value={`item-${groupIndex}`} className="border-0">
      <AccordionTrigger
        onClick={onToggle}
        className="group relative w-full cursor-pointer p-0 pr-3 text-start transition-colors hover:bg-gray-50 hover:no-underline"
      >
        <div className="relative mx-6 border-l border-gray-300 py-3 pl-4 pr-3">
          <div className="absolute -left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border border-gray-300 bg-white transition-colors group-hover:border-gray-400" />
          <div className="flex items-center gap-3">
            <ArchiveIcon className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-500" />
            <div className="flex flex-1 items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-500 transition-colors group-hover:text-gray-700">
                {count} hidden version{count > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
      </AccordionTrigger>

      <AccordionContent className="bg-gray-50/50 pb-0 pt-0">
        {archivedVersions.map(archivedVersion => {
          const isActiveVersion = Number(versionNumber) === archivedVersion.number;
          const isLatestVersion =
            archivedVersion.number === Math.max(...augmentedVersions.map(v => v.number));

          return (
            <div key={archivedVersion.id} className="opacity-70 hover:opacity-100">
              <VersionTab
                version={archivedVersion}
                isActiveVersion={isActiveVersion}
                isLatestVersion={isLatestVersion}
                versionNumber={versionNumber}
                counts={counts}
                navigateToVersionLink={navigateToVersionLink}
                onAddReleaseLabel={onAddReleaseLabel || (() => Promise.resolve())}
                handleDynamicReleaseLabelClick={handleDynamicReleaseLabelClick}
                dynamicReleaseLabelsRegistry={dynamicReleaseLabelsRegistry || {}}
                versionEvaluationScoresRegistry={versionEvaluationScoresRegistry || {}}
                handleABTestButtonClick={(testPromptId, versionId) =>
                  handleABTestButtonClick(testPromptId, versionId)
                }
                handleBacktestButtonClick={handleBacktestButtonClick}
                handleArchiveButtonClick={handleArchiveButtonClick}
                handleUnarchiveButtonClick={handleUnarchiveButtonClick}
                archivingVersionId={archivingVersionId}
                isArchiving={isArchiving}
                promptId={promptId}
                isWorkflow={isWorkflow}
                entityType={entityType}
                onConfigureLabel={onConfigureLabel}
                allLabels={allLabels}
                pendingReleaseLabelMoves={pendingReleaseLabelMoves}
                pendingDeletesByLabelId={pendingDeletesByLabelId}
                onPendingLabelClick={onPendingLabelClick}
              />
            </div>
          );
        })}
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export default ArchiveGroup;
